import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, fmtReporting } from "../lib/money";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { BandBadge, StatusChip } from "../components/Badges";
import { ArrowLeft, User, Phone, Mail, MessageSquare, Pencil } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function DealValue({ acct, onSaved }) {
  const { user } = useAuth();
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(acct.deal_value || 0);
  const [cfg, setCfg] = useState(null);
  const [cur, setCur] = useState(acct.deal_currency || "");
  useEffect(() => { api.get("/tenant/currency").then((r) => { setCfg(r.data); if (!cur) setCur(acct.deal_currency || r.data.region_currency_defaults?.[acct.region] || r.data.reporting_currency); }).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const reportingCurrency = cfg?.reporting_currency || "USD";
  const canEdit = user.role === "admin" || user.role === "supervisor";
  const save = async () => {
    const reason = window.prompt("Reason for manual deal value (optional)") ?? "";
    try { await api.patch(`/accounts/${acct.id}/deal-value`, { deal_value: Number(val), currency: cur || undefined, reason }); toast.success("Deal value updated"); setEdit(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="text-right" data-testid="deal-value-block">
      {edit ? (
        <div className="flex items-center gap-1 justify-end">
          {cfg?.multi && <select value={cur} onChange={(e) => setCur(e.target.value)} data-testid="deal-value-currency" className="border border-[#DFDAD0] px-1 py-1 font-mono text-xs">{cfg.all_currencies.map((c) => <option key={c} value={c}>{c}</option>)}</select>}
          <input type="number" value={val} onChange={(e) => setVal(e.target.value)} data-testid="deal-value-input" className="border border-[#DFDAD0] px-2 py-1 font-mono text-sm w-36" />
          <button onClick={save} data-testid="deal-value-save" className="bg-[#1C2530] text-[#F5F3EE] px-2 py-1 font-mono text-[10px] uppercase">Save</button>
          <button onClick={() => setEdit(false)} className="font-mono text-[10px] uppercase text-[#1C2530]/50 px-1">Cancel</button>
        </div>
      ) : (
        <div className="flex items-center gap-2 justify-end">
          <div className="font-mono text-2xl tabular-nums" data-testid="deal-value">{fmtMoney(acct.deal_value || 0, acct.deal_currency || reportingCurrency)}</div>
          {acct.deal_fx && fmtReporting(acct.deal_fx, reportingCurrency) && <div className="font-mono text-[10px] text-[#1C2530]/50" data-testid="deal-value-reporting">{fmtReporting(acct.deal_fx, reportingCurrency)}</div>}
          {canEdit && <button onClick={() => setEdit(true)} title="Manual override" data-testid="deal-value-edit" className="text-[#1C2530]/40 hover:text-[#1C2530]"><Pencil className="w-3.5 h-3.5" /></button>}
        </div>
      )}
      <div className="font-mono text-[9px] uppercase text-[#1C2530]/40" data-testid="deal-value-source">{acct.deal_value_source === "proposal" ? "from accepted proposal" : acct.deal_value_source === "manual" ? `manual · ${acct.deal_value_set_by || ""}` : "not set"}</div>
      <StatusChip status={acct.status} />
    </div>
  );
}

export default function AccountDetail() {
  const { id } = useParams();
  const [acct, setAcct] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get(`/accounts/${id}`).then((r) => setAcct(r.data)); }, [id]);

  if (!acct) return (<><PageHeader eyebrow="ACCOUNT" title="Loading…" /><div className="p-8"><Loading /></div></>);

  return (
    <>
      <PageHeader eyebrow={`${acct.industry} · ${acct.geography}`} title={acct.name}
        right={<DealValue acct={acct} onSaved={() => api.get(`/accounts/${id}`).then((r) => setAcct(r.data))} />} />
      <div className="p-8 space-y-6">
        <button onClick={() => nav("/accounts")} className="flex items-center gap-1 font-mono text-xs text-[#1C2530]/60 hover:text-[#1C2530]">
          <ArrowLeft className="w-3.5 h-3.5" /> ALL ACCOUNTS
        </button>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Contacts (Multi-Threading)</div>
          {acct.contacts.length === 0 ? <Empty /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {acct.contacts.map((c) => (
                <div key={c.id} className="border border-[#DFDAD0] bg-white p-4">
                  <div className="flex items-center gap-2"><User className="w-4 h-4 text-[#1C2530]/40" strokeWidth={1.5} />
                    <span className="font-medium text-[#1C2530]">{c.name}</span>
                    <span className="font-mono text-[9px] uppercase border border-[#DFDAD0] px-1 text-[#1C2530]/60">{c.seniority}</span>
                  </div>
                  <div className="text-xs text-[#1C2530]/60 mt-1">{c.title}</div>
                  <div className="mt-3 space-y-1 font-mono text-xs text-[#1C2530]/70">
                    <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{c.email}</div>
                    <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{c.phone}</div>
                    {c.botim_handle && <div className="flex items-center gap-2"><MessageSquare className="w-3 h-3" />Botim {c.botim_handle}</div>}
                  </div>
                  <div className="mt-2 font-mono text-[10px] text-[#1C2530]/40">SRC: {c.source}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Leads at this Account</div>
          <div className="border border-[#DFDAD0] bg-white">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                <th className="text-left px-4 py-2">Contact</th><th className="text-left px-4 py-2">Stage</th>
                <th className="text-left px-4 py-2">Band</th><th className="text-right px-4 py-2">Score</th><th className="text-left px-4 py-2">Status</th></tr></thead>
              <tbody>
                {acct.leads.map((l, i) => (
                  <tr key={l.id} onClick={() => nav(`/leads/${l.id}`)} className={`cursor-pointer hover:bg-[#EFECE5] border-t border-[#DFDAD0] ${i % 2 ? "bg-[#F5F3EE]/40" : ""}`}>
                    <td className="px-4 py-2">{acct.contacts.find((c) => c.id === l.contact_id)?.name || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{l.stage}</td>
                    <td className="px-4 py-2"><BandBadge band={l.band} /></td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{l.total_score}</td>
                    <td className="px-4 py-2"><StatusChip status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
